import { createContext, useContext, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';

import { DetailPanel } from '@/components/detail-panel';
import { OpenRow } from '@/components/open-row';
import { Pager } from '@/components/pager';
import { formatDate } from '@/components/status-timeline';
import {
  Badge, Callout, EmptyState, Field, buttonClass, inputClass, textareaClass,
} from '@/components/ui';
import { formatCentsOrNone } from '@/lib/money';
import { usePagedList } from '@/lib/use-paged-list';
import {
  adminShellQuery, prospectChoicesQuery, prospectQuery, prospectsQuery, tenantQuery,
  type Prospect, type ProspectChoice,
} from '@/queries';

/**
 * Prospects. Spec 009.
 *
 * Katherine and Jade meet businesses at gyms, door to door and at market
 * stalls. This is where those people go before they buy, and it replaces the
 * prototype Guy built with ChatGPT (Christian, 5 October 2026).
 *
 * Fast Add is the front door: one required field, made for a phone held in one
 * hand at a stall. The full form is for the office. A quote raised for a new
 * name lands here by itself (D-6), so this list fills without anybody filling
 * it in twice.
 */

type Search = { open?: string; add?: string; due?: string };

async function call(path: string, init: RequestInit) {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  const data = res.status === 204 ? null : await res.json().catch(() => null);
  // 409 is an answer here, not a failure: "this may be somebody we have".
  if (!res.ok && res.status !== 409) {
    throw new Error((data as { error?: { message?: string } } | null)?.error?.message
      ?? `Request failed (${res.status})`);
  }
  return { status: res.status, data } as { status: number; data: any };
}
const json = { 'content-type': 'application/json' };
const post = (path: string, body: unknown) =>
  call(path, { method: 'POST', headers: json, body: JSON.stringify(body) });
const patch = (path: string, body: unknown) =>
  call(path, { method: 'PATCH', headers: json, body: JSON.stringify(body) });
const del = (path: string) => call(path, { method: 'DELETE' });

function byList(choices: ProspectChoice[], list: string, keep?: string | null) {
  return choices.filter((c) => c.list === list && (c.active || c.id === keep));
}
function label(choices: ProspectChoice[], id: string | null | undefined) {
  return id ? choices.find((c) => c.id === id)?.label ?? '' : '';
}
function statusKey(choices: ProspectChoice[], id: string) {
  return choices.find((c) => c.id === id)?.key ?? null;
}

const TONE: Record<string, 'ok' | 'warn' | 'danger' | 'brand' | 'neutral'> = {
  new: 'brand', contact_attempted: 'neutral', in_conversation: 'neutral',
  quote_requested: 'warn', quoted: 'warn', won: 'ok', lost: 'danger', not_now: 'neutral',
};

/** Today, in the business's timezone from Settings, as YYYY-MM-DD. */
function today(zone?: string): string {
  return new Date().toLocaleDateString('en-CA', zone ? { timeZone: zone } : undefined);
}

/* -------------------------------------------------------------------------- */
/* The form, fast or full                                                     */
/* -------------------------------------------------------------------------- */

type Draft = Record<string, string>;

const FAST_FIELDS = ['name', 'contact_name', 'phone', 'email', 'street', 'suburb', 'postcode',
  'category_id', 'garment_interest', 'decoration_notes', 'notes', 'source_id'];

function blankDraft(choices: ProspectChoice[]): Draft {
  return {
    name: '', contact_name: '', contact_role: '', phone: '', email: '', street: '', suburb: '',
    postcode: '', website: '', category_id: '', subcategory: '', region_id: '', source_id: '',
    ownership_id: choices.find((c) => c.list === 'ownership' && c.key === 'unknown')?.id ?? '',
    operator: '', parent_group: '', group_locations: '', garment_interest: '',
    decoration_notes: '', notes: '', follow_up_on: '', follow_up_note: '',
  };
}

function draftOf(p: Prospect): Draft {
  const d: Draft = {};
  for (const [k, v] of Object.entries(p)) if (typeof v === 'string' || v === null) d[k] = v ?? '';
  return d;
}

/**
 * Fast add is used on a phone at a stall: 44 px to hit with a thumb, and 16 px
 * text, because iOS zooms the page into any field with smaller text and the
 * form then sits half off the screen. An inline style, because a height class
 * beside the shared input style loses to it.
 */
const BIG = { height: 44, fontSize: 16 };
const Big = createContext(false);

function Text({ d, set, k, label: l, hint, type = 'text', mode, required }: {
  d: Draft; set: (d: Draft) => void; k: string; label: string; hint?: string;
  type?: string; mode?: 'tel' | 'email' | 'numeric'; required?: boolean;
}) {
  const big = useContext(Big);
  return (
    <Field label={l} htmlFor={`pr-${k}`} hint={hint} optional={!required}>
      <input
        style={big ? BIG : undefined}
        id={`pr-${k}`} type={type} inputMode={mode} className={inputClass} value={d[k] ?? ''}
        onChange={(e) => set({ ...d, [k]: e.target.value })} autoComplete="off"
      />
    </Field>
  );
}

function Pick({ d, set, k, label: l, choices, list }: {
  d: Draft; set: (d: Draft) => void; k: string; label: string;
  choices: ProspectChoice[]; list: string;
}) {
  const big = useContext(Big);
  return (
    <Field label={l} htmlFor={`pr-${k}`} optional>
      <select
        style={big ? BIG : undefined}
        id={`pr-${k}`} className={inputClass} value={d[k] ?? ''}
        onChange={(e) => set({ ...d, [k]: e.target.value })}
      >
        <option value="">Not set</option>
        {byList(choices, list, d[k]).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
    </Field>
  );
}

function ProspectForm({
  choices, initial, mode, onSaved, onCancel, prospectId, onOpen,
}: {
  choices: ProspectChoice[];
  initial: Draft;
  mode: 'fast' | 'full' | 'edit';
  onSaved: (p: Prospect, again: boolean) => void;
  onCancel: () => void;
  prospectId?: string;
  onOpen: (id: string) => void;
}) {
  const [d, set] = useState<Draft>(initial);
  const [error, setError] = useState<string | null>(null);
  const [dupes, setDupes] = useState<{ prospects: any[]; customers: any[] } | null>(null);
  const full = mode !== 'fast';

  const save = useMutation({
    mutationFn: async ({ force, again }: { force: boolean; again: boolean }) => {
      const fields = full ? Object.keys(d) : FAST_FIELDS;
      const body: Record<string, unknown> = {};
      for (const k of fields) body[k] = d[k] ?? '';
      if (mode === 'edit') {
        const r = await patch(`/api/prospects/${prospectId}`, body);
        return { r, again };
      }
      const r = await post('/api/prospects', { ...body, force });
      return { r, again };
    },
    onSuccess: ({ r, again }) => {
      if (r.status === 409 && r.data?.duplicate) {
        setDupes({ prospects: r.data.prospects ?? [], customers: r.data.customers ?? [] });
        return;
      }
      setError(null); setDupes(null);
      onSaved(r.data.prospect as Prospect, again);
      if (again) set(blankDraft(choices));
    },
    // FR-904a: with no signal the save fails, and the form keeps what was typed.
    // A TypeError is the browser saying it reached nobody: Chrome says "Failed
    // to fetch" and Safari says "Load failed", so the type is the test.
    onError: (e: Error) => setError(
      e instanceof TypeError
        ? 'Not saved. No connection. Your details are still in the form.'
        : e.message,
    ),
  });

  function submit(again: boolean, force = false) {
    if (!d.name?.trim()) return setError('Give a name or a business name.');
    setError(null);
    save.mutate({ force, again });
  }

  return (
    <Big.Provider value={mode === 'fast'}>
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => { e.preventDefault(); submit(false); }}
      aria-label={mode === 'fast' ? 'Fast add a prospect' : 'Prospect details'}
    >
      {mode === 'fast' && (
        <p className="text-[13px] text-muted">
          Only the name is needed. Add the rest now if you have it, or later.
        </p>
      )}
      {error && <Callout tone="danger" title={error} />}
      {dupes && (
        <Callout tone="warn" title="This may be somebody we have already">
          <ul className="mt-2 flex flex-col gap-1 text-[13px]">
            {dupes.prospects.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-2">
                <span>Prospect: {p.name}{p.suburb ? `, ${p.suburb}` : ''} {p.email ?? ''} {p.phone ?? ''}</span>
                <button type="button" className="underline" onClick={() => onOpen(p.id)}>Open</button>
              </li>
            ))}
            {dupes.customers.map((c) => (
              <li key={c.id}>Customer: {c.name} {c.email ?? ''} {c.phone ?? ''}</li>
            ))}
          </ul>
          <div className="mt-3 flex gap-2">
            <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => submit(false, true)}>
              Save anyway
            </button>
            <button type="button" className={buttonClass('tertiary', 'sm')} onClick={() => setDupes(null)}>
              Go back
            </button>
          </div>
        </Callout>
      )}

      <Text d={d} set={set} k="name" label="Name or business" required
        hint="A business, or the person's own name if there is no business." />
      <div className="grid gap-4 sm:grid-cols-2">
        <Text d={d} set={set} k="contact_name" label="Person spoken to" />
        {full && <Text d={d} set={set} k="contact_role" label="Their role" hint="Owner, manager, coach" />}
        <Text d={d} set={set} k="phone" label="Phone" type="tel" mode="tel" />
        <Text d={d} set={set} k="email" label="Email" type="email" mode="email" />
      </div>
      <Text d={d} set={set} k="street" label="Street address" />
      <div className="grid grid-cols-[1fr_120px] gap-4">
        <Text d={d} set={set} k="suburb" label="Suburb" />
        <Text d={d} set={set} k="postcode" label="Postcode" mode="numeric" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Pick d={d} set={set} k="category_id" label="Category" choices={choices} list="category" />
        {full && <Text d={d} set={set} k="subcategory" label="Subcategory" hint="24-hour gym, netball club" />}
        {full && <Pick d={d} set={set} k="region_id" label="Region" choices={choices} list="region" />}
        <Pick d={d} set={set} k="source_id" label="Where we met them" choices={choices} list="source" />
      </div>
      <Text d={d} set={set} k="garment_interest" label="Garments they are interested in"
        hint="Singlets, hoodies, polos" />
      <Field label="Decoration notes" htmlFor="pr-decoration_notes" optional>
        <textarea
          id="pr-decoration_notes" rows={2} className={textareaClass} value={d.decoration_notes ?? ''}
          onChange={(e) => set({ ...d, decoration_notes: e.target.value })}
        />
      </Field>
      <Field label="Notes" htmlFor="pr-notes" optional>
        <textarea
          id="pr-notes" rows={3} className={textareaClass} value={d.notes ?? ''}
          onChange={(e) => set({ ...d, notes: e.target.value })}
        />
      </Field>

      {full && (
        <details className="rounded-[10px] border border-line p-3" open={mode === 'full'}>
          <summary className="cursor-pointer text-[13px] font-semibold">Business research</summary>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Text d={d} set={set} k="website" label="Website" type="url" />
            <Pick d={d} set={set} k="ownership_id" label="Ownership type" choices={choices} list="ownership" />
            <Text d={d} set={set} k="operator" label="Owner or operator" />
            <Text d={d} set={set} k="parent_group" label="Parent chain or group" />
            <Text d={d} set={set} k="group_locations" label="Locations in the group" />
          </div>
        </details>
      )}

      {full && (
        <div className="grid gap-4 sm:grid-cols-[180px_1fr]">
          <Text d={d} set={set} k="follow_up_on" label="Follow up on" type="date" />
          <Text d={d} set={set} k="follow_up_note" label="What to follow up" />
        </div>
      )}

      <div className="sticky bottom-0 -mx-4 flex flex-wrap gap-2 border-t border-line bg-panel px-4 py-3">
        <button type="submit" className={buttonClass('primary', 'md', 'h-12 flex-1 sm:flex-none')} disabled={save.isPending}>
          {save.isPending ? 'Saving' : mode === 'edit' ? 'Save changes' : 'Save prospect'}
        </button>
        {mode === 'fast' && (
          <button
            type="button" className={buttonClass('secondary', 'md', 'h-12')}
            disabled={save.isPending} onClick={() => submit(true)}
          >
            Save and add another
          </button>
        )}
        <button type="button" className={buttonClass('tertiary', 'md', 'h-12')} onClick={onCancel}>Cancel</button>
      </div>
    </form>
    </Big.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/* One prospect                                                               */
/* -------------------------------------------------------------------------- */

const KIND_LABEL: Record<string, string> = {
  visit: 'Visit', phone: 'Phone call', email: 'Email', message: 'Message', note: 'Note',
  status: 'Status', quote: 'Quote',
};

function ProspectRecord({ id, choices, onOpen }: {
  id: string; choices: ProspectChoice[]; onOpen: (id: string) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useSuspenseQuery(adminShellQuery).data;
  const zone = useSuspenseQuery(tenantQuery).data.timezone;
  const mayEdit = (can.prospects ?? []).includes('edit');
  const mayDelete = (can.prospects ?? []).includes('delete');
  const mayQuote = (can.quotes ?? []).includes('create');
  const { data, isPending, error } = useQuery(prospectQuery(id));
  const [editing, setEditing] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [kind, setKind] = useState('phone');
  const [note, setNote] = useState('');
  const [nextOn, setNextOn] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [contact, setContact] = useState({ name: '', role: '', phone: '', email: '', decision_maker: false });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'prospect', id] });
    void qc.invalidateQueries({ queryKey: ['admin', 'prospects'] });
  };
  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => patch(`/api/prospects/${id}`, body),
    onSuccess: () => { setSaid(null); setPendingStatus(null); setLostReason(''); refresh(); },
    onError: (e: Error) => setSaid(e.message),
  });
  const log = useMutation({
    mutationFn: () => post(`/api/prospects/${id}/activities`,
      { kind, note, ...(nextOn ? { follow_up_on: nextOn } : {}) }),
    onSuccess: () => { setNote(''); setNextOn(''); setSaid(null); refresh(); },
    onError: (e: Error) => setSaid(e.message),
  });
  const addContact = useMutation({
    mutationFn: () => post(`/api/prospects/${id}/contacts`, contact),
    onSuccess: () => { setContact({ name: '', role: '', phone: '', email: '', decision_maker: false }); refresh(); },
    onError: (e: Error) => setSaid(e.message),
  });
  const dropContact = useMutation({
    mutationFn: (cid: string) => del(`/api/prospect-contacts/${cid}`),
    onSuccess: refresh,
    onError: (e: Error) => setSaid(e.message),
  });

  if (isPending) return <p className="p-4 text-[13px] text-muted">Reading the prospect.</p>;
  if (error || !data) return <Callout tone="danger" title={(error as Error)?.message ?? 'Not found'} />;
  const p = data.prospect;
  const key = statusKey(choices, p.status_id);
  const address = [p.street, p.suburb, p.postcode].filter(Boolean).join(', ');
  const unknown = <span className="text-muted">Unknown / unverified</span>;

  if (editing) {
    return (
      <div className="p-4">
        <ProspectForm
          choices={choices} initial={draftOf(p)} mode="edit" prospectId={id} onOpen={onOpen}
          onSaved={() => { setEditing(false); refresh(); }} onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  const row = (l: string, v: React.ReactNode) => (
    <div className="grid grid-cols-[150px_1fr] gap-3 py-1.5 text-[13.5px]">
      <dt className="text-muted">{l}</dt><dd className="min-w-0 break-words">{v || unknown}</dd>
    </div>
  );

  return (
    <div className="flex flex-col gap-6 p-4">
      {said && <Callout tone="danger" title={said} />}
      {!p.active && <Callout tone="warn" title="This prospect is removed. It does not show in the list." />}

      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={TONE[key ?? ''] ?? 'neutral'}>{label(choices, p.status_id)}</Badge>
        {label(choices, p.category_id) && <span className="text-[13px] text-muted">{label(choices, p.category_id)}</span>}
        <span className="flex-1" />
        {p.phone && <a className={buttonClass('secondary', 'sm')} href={`tel:${p.phone.replace(/\s/g, '')}`}>Call</a>}
        {p.email && <a className={buttonClass('secondary', 'sm')} href={`mailto:${p.email}`}>Email</a>}
        {p.website && <a className={buttonClass('secondary', 'sm')} href={/^https?:/.test(p.website) ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer">Website</a>}
        {address && (
          <a className={buttonClass('secondary', 'sm')} target="_blank" rel="noreferrer"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}>Map</a>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {mayQuote && p.active && (
          <button
            type="button" className={buttonClass('primary', 'sm')}
            onClick={() => void navigate({ to: '/admin/quotes', search: { for_prospect: id } as never })}
          >
            Start a quote
          </button>
        )}
        {mayEdit && <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => setEditing(true)}>Edit details</button>}
        {mayEdit && (
          <div className="w-[190px]">
          <select
            aria-label="Change the status" className={`${inputClass} text-[13px]`}
            value={pendingStatus ?? p.status_id}
            onChange={(e) => {
              const v = e.target.value;
              if (statusKey(choices, v) === 'lost') setPendingStatus(v);
              else update.mutate({ status_id: v });
            }}
          >
            {byList(choices, 'status', p.status_id).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          </div>
        )}
      </div>
      {pendingStatus && (
        <div className="flex flex-col gap-2 rounded-[10px] border border-line p-3">
          <Field label="Why was it lost?" htmlFor="pr-lost" hint="The next person to call them needs this.">
            <input id="pr-lost" className={inputClass} value={lostReason} onChange={(e) => setLostReason(e.target.value)} />
          </Field>
          <div className="flex gap-2">
            <button type="button" className={buttonClass('primary', 'sm')} disabled={!lostReason.trim() || update.isPending}
              onClick={() => update.mutate({ status_id: pendingStatus, lost_reason: lostReason })}>Mark as lost</button>
            <button type="button" className={buttonClass('tertiary', 'sm')} onClick={() => setPendingStatus(null)}>Cancel</button>
          </div>
        </div>
      )}

      <section aria-labelledby="pr-follow">
        <h3 id="pr-follow" className="mb-2 text-[14px] font-semibold">Follow-up</h3>
        <p className={`text-[13.5px] ${p.follow_up_on && p.follow_up_on <= today(zone) ? 'font-semibold text-[color:var(--color-danger-ink)]' : ''}`}>
          {p.follow_up_on
            ? `${formatDate(p.follow_up_on)}${p.follow_up_on <= today(zone) ? ' (due)' : ''}${p.follow_up_note ? `: ${p.follow_up_note}` : ''}`
            : 'No follow-up set.'}
        </p>
      </section>

      {mayEdit && p.active && (
        <section aria-labelledby="pr-log" className="rounded-[10px] border border-line bg-bg p-3">
          <h3 id="pr-log" className="mb-2 text-[14px] font-semibold">Log a contact</h3>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="How">
            {['phone', 'visit', 'email', 'message', 'note'].map((k) => (
              <button key={k} type="button" aria-pressed={kind === k}
                className={buttonClass(kind === k ? 'primary' : 'secondary', 'sm')} onClick={() => setKind(k)}>
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
          <textarea
            aria-label="What happened" rows={2} className={`${textareaClass} mt-2`} value={note}
            placeholder="What was said, and what happens next" onChange={(e) => setNote(e.target.value)}
          />
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <Field label="Next follow-up" htmlFor="pr-next" optional>
              <input id="pr-next" type="date" className={inputClass} style={{ width: 170 }} value={nextOn}
                onChange={(e) => setNextOn(e.target.value)} />
            </Field>
            <button type="button" className={buttonClass('primary', 'sm')} disabled={log.isPending}
              onClick={() => log.mutate()}>{log.isPending ? 'Saving' : 'Save'}</button>
          </div>
        </section>
      )}

      <section aria-labelledby="pr-details">
        <h3 id="pr-details" className="mb-1 text-[14px] font-semibold">Organisation and contact</h3>
        <dl className="divide-y divide-[color:var(--color-line-soft)]">
          {row('Name or business', p.name)}
          {row('Person spoken to', [p.contact_name, p.contact_role].filter(Boolean).join(', '))}
          {row('Phone', p.phone)}
          {row('Email', p.email)}
          {row('Address', address)}
          {row('Region', label(choices, p.region_id))}
          {row('Subcategory', p.subcategory)}
          {row('Where we met them', label(choices, p.source_id))}
          {row('Garments', p.garment_interest)}
          {row('Decoration notes', p.decoration_notes)}
          {row('Notes', p.notes && <span className="whitespace-pre-line">{p.notes}</span>)}
          {key === 'lost' && row('Why it was lost', p.lost_reason)}
        </dl>
      </section>

      <section aria-labelledby="pr-research">
        <h3 id="pr-research" className="mb-1 text-[14px] font-semibold">Ownership and research</h3>
        <dl className="divide-y divide-[color:var(--color-line-soft)]">
          {row('Website', p.website)}
          {row('Ownership type', label(choices, p.ownership_id))}
          {row('Owner or operator', p.operator)}
          {row('Parent chain or group', p.parent_group)}
          {row('Locations in group', p.group_locations)}
        </dl>
      </section>

      <section aria-labelledby="pr-people">
        <h3 id="pr-people" className="mb-2 text-[14px] font-semibold">Other people there</h3>
        {data.contacts.length === 0 && <p className="text-[13px] text-muted">None added.</p>}
        <ul className="flex flex-col gap-1.5">
          {data.contacts.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2 rounded-[8px] bg-bg px-3 py-2 text-[13.5px]">
              <span className="font-medium">{c.name}</span>
              {c.role && <span className="text-muted">{c.role}</span>}
              {c.decision_maker && <Badge tone="brand">decision maker</Badge>}
              <span className="flex-1" />
              {c.phone && <a className="underline" href={`tel:${c.phone.replace(/\s/g, '')}`}>{c.phone}</a>}
              {c.email && <a className="underline" href={`mailto:${c.email}`}>{c.email}</a>}
              {mayEdit && (
                <button type="button" className="text-[12px] text-muted underline" aria-label={`Remove ${c.name}`}
                  onClick={() => dropContact.mutate(c.id)}>Remove</button>
              )}
            </li>
          ))}
        </ul>
        {mayEdit && p.active && (
          <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]">
            <input aria-label="Name" placeholder="Name" className={inputClass} value={contact.name}
              onChange={(e) => setContact({ ...contact, name: e.target.value })} />
            <input aria-label="Role" placeholder="Role" className={inputClass} value={contact.role}
              onChange={(e) => setContact({ ...contact, role: e.target.value })} />
            <input aria-label="Phone" placeholder="Phone" inputMode="tel" className={inputClass} value={contact.phone}
              onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
            <input aria-label="Email" placeholder="Email" inputMode="email" className={inputClass} value={contact.email}
              onChange={(e) => setContact({ ...contact, email: e.target.value })} />
            <button type="button" className={buttonClass('secondary', 'sm')} disabled={!contact.name.trim() || addContact.isPending}
              onClick={() => addContact.mutate()}>Add</button>
            <label className="flex items-center gap-2 text-[12.5px] sm:col-span-5">
              <input type="checkbox" checked={contact.decision_maker}
                onChange={(e) => setContact({ ...contact, decision_maker: e.target.checked })} />
              Makes the buying decision
            </label>
          </div>
        )}
      </section>

      <section aria-labelledby="pr-quotes">
        <h3 id="pr-quotes" className="mb-2 text-[14px] font-semibold">Quotes</h3>
        {data.quotes.length === 0 && <p className="text-[13px] text-muted">No quote yet.</p>}
        <ul className="flex flex-col gap-1.5">
          {data.quotes.map((q) => (
            <li key={q.id} className="flex items-center gap-3 rounded-[8px] bg-bg px-3 py-2 text-[13.5px]">
              <Link to="/admin/quotes/$ref" params={{ ref: q.ref }} className="font-mono underline">{q.ref}</Link>
              <span className="text-muted">{q.status.replace(/_/g, ' ')}</span>
              <span className="flex-1" />
              <span>{formatCentsOrNone(q.total_cents ?? q.est_total_cents)}</span>
              <span className="text-[12px] text-muted">{formatDate(q.created_at)}</span>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="pr-history">
        <h3 id="pr-history" className="mb-2 text-[14px] font-semibold">History</h3>
        <ol className="flex flex-col gap-2">
          {data.activities.map((a) => (
            <li key={a.id} className="border-l-2 border-line pl-3 text-[13px]">
              <span className="font-medium">{KIND_LABEL[a.kind] ?? a.kind}</span>
              <span className="ml-2 text-[12px] text-muted">
                {new Date(a.created_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}
              </span>
              {a.note && <p className="mt-0.5 whitespace-pre-line">{a.note}</p>}
            </li>
          ))}
        </ol>
      </section>

      {(mayDelete || (!p.active && mayEdit)) && (
        <div className="border-t border-line pt-3">
          {p.active ? (
            <button type="button" className="text-[12.5px] text-[color:var(--color-danger-ink)] underline"
              onClick={() => update.mutate({ active: false })}>Remove this prospect</button>
          ) : (
            <button type="button" className={buttonClass('secondary', 'sm')}
              onClick={() => update.mutate({ active: true })}>Restore this prospect</button>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The lists, for an administrator (D-3)                                      */
/* -------------------------------------------------------------------------- */

const LISTS: Array<[string, string]> = [
  ['category', 'Category'], ['region', 'Region'], ['status', 'Status'],
  ['source', 'Where we met them'], ['ownership', 'Ownership type'],
];

function ListsEditor({ choices }: { choices: ProspectChoice[] }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState<Record<string, string>>({});
  const [said, setSaid] = useState<string | null>(null);
  const done = () => {
    setSaid(null);
    void qc.invalidateQueries({ queryKey: ['admin', 'prospects'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'prospect-choices'] });
  };
  const add = useMutation({
    mutationFn: (v: { list: string; label: string }) => post('/api/prospect-choices', v),
    onSuccess: (_r, v) => { setAdding((a) => ({ ...a, [v.list]: '' })); done(); },
    onError: (e: Error) => setSaid(e.message),
  });
  const change = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) => patch(`/api/prospect-choices/${v.id}`, v.body),
    onSuccess: done,
    onError: (e: Error) => setSaid(e.message),
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] text-muted">
        These are the choices in the prospect forms and filters. A removed choice
        stays on the prospects that already have it.
      </p>
      {said && <Callout tone="danger" title={said} />}
      <div className="grid gap-4 lg:grid-cols-2">
        {LISTS.map(([list, title]) => (
          <div key={list} className="rounded-[10px] border border-line bg-panel p-3">
            <h3 className="mb-2 text-[14px] font-semibold">{title}</h3>
            <ul className="flex flex-col gap-1">
              {choices.filter((c) => c.list === list).map((c) => (
                <li key={c.id} className={`flex items-center gap-2 text-[13.5px] ${c.active ? '' : 'opacity-50'}`}>
                  <input
                    aria-label={`Rename ${c.label}`} defaultValue={c.label} className={`${inputClass} h-8 flex-1`}
                    onBlur={(e) => { if (e.target.value.trim() && e.target.value.trim() !== c.label) change.mutate({ id: c.id, body: { label: e.target.value } }); }}
                  />
                  {c.key
                    ? <span className="w-[76px] text-right text-[11.5px] text-muted" title="The system uses this one">built in</span>
                    : (
                      <button type="button" className="w-[76px] text-right text-[12px] underline"
                        onClick={() => change.mutate({ id: c.id, body: { active: !c.active } })}>
                        {c.active ? 'Remove' : 'Restore'}
                      </button>
                    )}
                </li>
              ))}
            </ul>
            <form className="mt-2 flex gap-2" onSubmit={(e) => {
              e.preventDefault();
              if ((adding[list] ?? '').trim()) add.mutate({ list, label: adding[list]! });
            }}>
              <input aria-label={`New ${title.toLowerCase()} choice`} placeholder="New choice" className={`${inputClass} h-8 flex-1`}
                value={adding[list] ?? ''} onChange={(e) => setAdding((a) => ({ ...a, [list]: e.target.value }))} />
              <button type="submit" className={buttonClass('secondary', 'sm')}>Add</button>
            </form>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The page                                                                   */
/* -------------------------------------------------------------------------- */

export default function AdminProspectsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Search;
  const { can } = useSuspenseQuery(adminShellQuery).data;
  const zone = useSuspenseQuery(tenantQuery).data.timezone;
  const mayCreate = (can.prospects ?? []).includes('create');
  const mayLists = (can.settings ?? []).includes('edit');
  const choicesQ = useQuery(prospectChoicesQuery);

  const [category, setCategory] = useState('');
  const [region, setRegion] = useState('');
  const [status, setStatus] = useState('');
  const [contacted, setContacted] = useState('');
  const [retired, setRetired] = useState(false);
  const [showLists, setShowLists] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const due = search.due === '1';

  const list = usePagedList<Prospect>(
    ({ page, q }) => prospectsQuery({ page, q, category, region, status, contacted, due, retired }) as never,
    [category, region, status, contacted, due, retired],
  );
  const data = list.data as Awaited<ReturnType<NonNullable<ReturnType<typeof prospectsQuery>['queryFn']>>> | undefined;
  const choices = data?.choices ?? choicesQ.data?.choices ?? [];

  const go = (s: Partial<Search>) => void navigate({ to: '/admin/prospects', search: { ...search, ...s } as never });
  const openId = search.open ?? null;
  const adding = search.add === 'fast' || search.add === 'full' ? search.add : null;

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(null), 6000);
    return () => clearTimeout(t);
  }, [saved]);

  const stat = (l: string, n: number | undefined, active: boolean, onClick: () => void) => (
    <button
      type="button" onClick={onClick} aria-pressed={active}
      className={`rounded-[10px] border p-4 text-left ${active ? 'border-ink bg-brand-wash-2' : 'border-line bg-panel hover:border-line-strong'}`}
    >
      <p className="text-[12px] uppercase tracking-wide text-muted">{l}</p>
      <p className="mt-1 text-[28px] font-extrabold tracking-[-0.02em]">{n ?? '…'}</p>
    </button>
  );

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Prospects</h1>
          <p className="mt-1 text-[12.5px] text-muted">
            People and businesses we have not sold to yet. Find the next conversation, and keep every detail in one place.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {mayLists && (
            <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => setShowLists((v) => !v)}>
              {showLists ? 'Back to prospects' : 'Edit the lists'}
            </button>
          )}
          {mayCreate && (
            <>
              <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => go({ add: 'full' })}>Add with full details</button>
              <button type="button" className={buttonClass('primary', 'sm')} onClick={() => go({ add: 'fast' })}>Fast add</button>
            </>
          )}
        </div>
      </div>

      {saved && <Callout tone="ok" title={`Saved: ${saved}`} className="mb-4" />}

      {showLists ? <ListsEditor choices={choices} /> : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-3">
            {stat('Total prospects', data?.stats.total, !due && !contacted, () => { setContacted(''); go({ due: undefined }); })}
            {stat('Not contacted', data?.stats.not_contacted, contacted === 'no', () => { setContacted('no'); go({ due: undefined }); })}
            {stat('Follow-ups due', data?.stats.due, due, () => { setContacted(''); go({ due: '1' }); })}
          </div>

          {data && data.regions.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1.5" role="group" aria-label="Region">
              {data.regions
                .sort((a, b) => b.n - a.n)
                .map((r) => (
                  <button key={r.id} type="button" aria-pressed={region === r.id}
                    className={buttonClass(region === r.id ? 'primary' : 'secondary', 'sm')}
                    onClick={() => setRegion(region === r.id ? '' : r.id === 'none' ? '' : r.id)}>
                    {r.id === 'none' ? 'No region' : label(choices, r.id)} · {r.n}
                  </button>
                ))}
            </div>
          )}

          <div className="mb-3 flex flex-wrap items-center gap-2">
            <input className={`${inputClass} w-full sm:w-[280px]`} placeholder="Search names, contacts, notes, suburbs"
              value={list.query} onChange={(e) => list.setQuery(e.target.value)} aria-label="Search prospects" />
            {/* Each in a box of its own width: the shared input style is full
                width, and a width class beside it loses to it. */}
            {[['Category', 'category', category, setCategory], ['Status', 'status', status, setStatus]].map(([l, k, v, setV]) => (
              <div key={k as string} className="w-[calc(50%-4px)] sm:w-[190px]">
                <select aria-label={l as string} className={inputClass} value={v as string}
                  onChange={(e) => (setV as (s: string) => void)(e.target.value)}>
                  <option value="">Any {(l as string).toLowerCase()}</option>
                  {byList(choices, k as string).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            ))}
            <div className="w-[calc(50%-4px)] sm:w-[170px]">
              <select aria-label="Contacted" className={inputClass} value={contacted} onChange={(e) => setContacted(e.target.value)}>
                <option value="">Contacted or not</option>
                <option value="yes">Contacted</option>
                <option value="no">Not contacted</option>
              </select>
            </div>
            <label className="ml-auto flex items-center gap-2 text-[13px] text-muted">
              <input type="checkbox" checked={retired} onChange={(e) => setRetired(e.target.checked)} />
              Show removed
            </label>
          </div>

          {list.error && <Callout tone="danger" title={list.error.message} className="mb-4" />}

          <div className="rounded-[10px] border border-line bg-panel">
            {list.rows.length === 0 && list.data !== undefined ? (
              <EmptyState
                title={data?.stats.total === 0 ? 'No prospects yet' : 'No prospect matches that'}
                body={data?.stats.total === 0
                  ? 'Use Fast add at a market stall or after a visit. A quote raised for a new name also lands here.'
                  : 'Try fewer filters, or search by phone or suburb.'}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th scope="col">Name or business</th>
                      <th scope="col">Contact</th>
                      <th scope="col">Suburb</th>
                      <th scope="col">Status</th>
                      <th scope="col">Follow-up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.rows.map((p) => {
                      const k = statusKey(choices, p.status_id);
                      const late = p.follow_up_on && p.follow_up_on <= today(zone);
                      return (
                        <OpenRow key={p.id} label={`Open ${p.name}`} onOpen={() => go({ open: p.id })}
                          className={p.active ? '' : 'opacity-60'}>
                          <td>
                            <div className="text-[13.5px] font-medium">{p.name}</div>
                            <div className="text-[12px] text-muted">
                              {[label(choices, p.category_id), p.subcategory].filter(Boolean).join(' · ')}
                            </div>
                          </td>
                          <td className="text-[12.5px]">
                            <div>{p.contact_name ?? ''}</div>
                            <div className="text-muted">{p.phone ?? ''}{p.phone && p.email ? ' · ' : ''}{p.email ?? ''}</div>
                          </td>
                          <td className="text-[12.5px] text-muted">{[p.suburb, p.postcode].filter(Boolean).join(' ')}</td>
                          <td><Badge tone={TONE[k ?? ''] ?? 'neutral'}>{label(choices, p.status_id)}</Badge></td>
                          <td className={`text-[12.5px] ${late ? 'font-semibold text-[color:var(--color-danger-ink)]' : 'text-muted'}`}>
                            {p.follow_up_on ? formatDate(p.follow_up_on) : ''}
                          </td>
                        </OpenRow>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Pager {...list.pager} noun="prospect" onPage={list.setPage} />
          </div>
        </>
      )}

      <DetailPanel
        open={adding != null} onClose={() => go({ add: undefined })}
        label={adding === 'fast' ? 'Fast add a prospect' : 'Add a prospect'}
        title={<span className="font-semibold">{adding === 'fast' ? 'Fast add' : 'Add a prospect'}</span>}
        width={adding === 'full' ? 'wide' : 'normal'}
      >
        {adding && choices.length > 0 && (
          <div className="p-4">
            <ProspectForm
              key={adding} choices={choices} initial={blankDraft(choices)} mode={adding}
              onOpen={(id) => go({ add: undefined, open: id })}
              onSaved={(p, again) => {
                setSaved(p.name);
                void qc.invalidateQueries({ queryKey: ['admin', 'prospects'] });
                if (!again) go({ add: undefined });
              }}
              onCancel={() => go({ add: undefined })}
            />
          </div>
        )}
      </DetailPanel>

      <DetailPanel
        open={openId != null} onClose={() => go({ open: undefined })}
        label="Prospect" width="wide"
        title={<span className="font-semibold">{list.rows.find((r) => r.id === openId)?.name ?? 'Prospect'}</span>}
      >
        {openId && choices.length > 0 && (
          <ProspectRecord key={openId} id={openId} choices={choices} onOpen={(id) => go({ open: id })} />
        )}
      </DetailPanel>
    </div>
  );
}
